'use client'

/**
 * THE AGREEMENT PAGE'S TRANSPORT: two session-lane groups (cutover note 01
 * E.1, E.2; research 01 §2.3.6).
 *
 *   - `cutoverAgreementViews`: where the firm's move stands (the page and the
 *     root banner read it), the administrator's saved match answers (decision
 *     041) and the agreement;
 *   - `cutoverMatcherViews`: the proposal the page resolves, read from the
 *     matcher's own group so the page and the server check the same thing.
 *
 * THE CONTRACTS ARE IMPORTED BY PATH, never through a leaf's barrel, which
 * publishes repositories that reach `@effect/sql-pg`.
 *
 * NO POLL. The status changes when support requests or runs the move, a few
 * times in a firm's life; a 5 minute freshness is enough for a banner, and
 * the agreement write sets the answer it returns straight into the cache.
 */

import {
  CutoverAgreementViewsApi,
} from '@/contexts/bms/cutover/agreement/presentation/agreement-views-api'
import { Effect } from 'effect'

import type { AgreePayload, AgreementStatus } from '@/contexts/bms/cutover/agreement/domain/agreement'
import type { SaveMatchChoicePayload } from '@/contexts/bms/cutover/agreement/domain/match-draft'
import { CutoverMatcherViewsApi } from '@/contexts/bms/cutover/matcher/presentation/matcher-views-api'
import { createHttpApiQuery } from '@/lib/api/http-api-query'

export const cutoverAgreementEq = createHttpApiQuery(CutoverAgreementViewsApi)
export const cutoverMatcherEq = createHttpApiQuery(CutoverMatcherViewsApi)

/**
 * The organisation key a query uses before the identity has an org id. The
 * queries that use it are disabled until the real id arrives, so it is never
 * fetched.
 */
export const NO_ORG = '00000000-0000-4000-a000-000000000000'

export const CUTOVER_STATUS_STALE_MS = 5 * 60_000

export const cutoverAgreementKeys = {
  status: (organizationId: string) => ['bms.cutover.agreement.status', organizationId] as const,
  proposal: (organizationId: string) => ['bms.cutover.matcher.proposal', organizationId] as const,
  draft: (organizationId: string) => ['bms.cutover.agreement.draft', organizationId] as const,
}

export const cutoverStatusQueryOptions = (organizationId: string) =>
  cutoverAgreementEq.queryOptions({
    queryKey: cutoverAgreementKeys.status(organizationId),
    staleTime: CUTOVER_STATUS_STALE_MS,
    request: (client) => client.cutoverAgreementViews.getAgreementStatus({ path: { organizationId } }),
  })

/** The proposal is recomputed on every read; it is fresh while the page is open and never cached across visits. */
export const cutoverProposalQueryOptions = (organizationId: string) =>
  cutoverMatcherEq.queryOptions({
    queryKey: cutoverAgreementKeys.proposal(organizationId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    request: (client) => client.cutoverMatcherViews.getMatchProposal({ path: { organizationId } }),
  })

/**
 * The administrator's saved match answers (decision 041), read once with the
 * proposal they are checked against. Like the proposal, no refetch on focus:
 * a fresh draft against a page's older proposal would read a colleague's
 * answers as changed.
 */
export const cutoverDraftQueryOptions = (organizationId: string) =>
  cutoverAgreementEq.queryOptions({
    queryKey: cutoverAgreementKeys.draft(organizationId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    request: (client) => client.cutoverAgreementViews.getMatchDraft({ path: { organizationId } }),
  })

/** One answer to save, or `payload: null` to clear the legacy client's answer. */
export interface PersistMatchChoice {
  readonly legacyId: string
  readonly payload: SaveMatchChoicePayload | null
}

/**
 * Saves or clears one answer as it is made. The writes run one at a time in
 * the order made (`scope`), so two quick clicks on one match land in order and
 * the last answer is the one saved.
 */
export const persistMatchChoiceMutationOptions = (organizationId: string) => ({
  ...cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.persistMatchChoice', organizationId],
    request: (client, { legacyId, payload }: PersistMatchChoice) =>
      payload === null
        ? client.cutoverAgreementViews.clearMatchChoice({ path: { organizationId, legacyId } }).pipe(Effect.as(legacyId))
        : client.cutoverAgreementViews.saveMatchChoice({ path: { organizationId, legacyId }, payload }).pipe(Effect.as(legacyId)),
  }),
  scope: { id: `bms.cutover.agreement.draft.${organizationId}` },
})

export const agreeMutationOptions = (organizationId: string, onAgreed: (status: AgreementStatus) => void) =>
  cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.agree', organizationId],
    request: (client, payload: AgreePayload) =>
      client.cutoverAgreementViews.agree({ path: { organizationId }, payload }),
    onSuccess: (status) => onAgreed(status),
  })

/** The message a refused call carries, from the BMS envelope, or a plain fallback. */
export const messageOf = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string' && error.message !== '') {
    return error.message
  }
  return fallback
}

/** Whether a refused call was the 403 a non-administrator gets. */
export const isForbidden = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && '_tag' in error && error._tag === 'BmsApiForbiddenError'
